import { IFilterConfigItem } from '@/bundles/GroupFilters/interfaces/IFilterConfigItem';
import { FilterCategoryEnum, FilterTypeEnum } from '@/bundles/GroupFilters/enums/FilterTypeEnum';
import { IFilterButtonGroupItems } from '@/bundles/GroupFilters/interfaces/inputs/IFilterButtonGroupItems';
import { FilterOfficeEnterpriseItems } from '@/bundles/GroupFilters/enums/FilterOfficeEnterpriseEnum';
import { ContactSpatialSearchRecordTypeEnum } from '@/bundles/GroupFilters/enums/ContactSpatialSearchRecordTypeEnum';
import { FilterBrokerageItems } from '@/bundles/GroupFilters/enums/FilterBrokerageEnum';
import { Nullable } from '@/utils/types';
import { formatToSF } from '@/bundles/GroupFilters/helpers/formatters/formatToSF';
import { getOccupancyTypeFilterItems } from '@/bundles/Occupier/helpers/getOccupancyTypeFilterItems';
import {
  FilterPrimaryUseClassItems,
  getCountriesForFilters,
  getMarketsFilters,
  getPropertyTypesWithMixedUseForFilters,
  getPropertyUsesForFilters,
  PROPERTY_TYPE_KEY,
} from '@/bundles/GroupFilters/consts';
import { getRailsStatusItems } from '@/bundles/Property/helpers';
import i18n from '@/bundles/Localization';
import { LocalizationEnum } from '@/bundles/Localization/enum/LocalizationEnum';
import {
  FilterAllExcludeFilterEnum,
  FilterConsultingButtonItems,
  FilterInvestmentSaleButtonItems,
  FilterSubleaseButtonItems,
  FilterTransactionTypeEnum,
  FilterTransactionTypeLabels
} from '@/bundles/GroupFilters/enums/transactionType';
import { getIndustryTypes } from '@/bundles/Companies/types/companyIndustryType';
import { FilterPrimaryUseClassEnum } from '@/bundles/GroupFilters/types';
import { IGroupFilterItem } from '@/bundles/GroupFilters/helpers';
import { occupierTplTypeLabels } from '@/bundles/Occupier/consts';
import { pipelineStageLabels } from '@/bundles/Pipelines/types/stageEnum';
import { ITextValue } from '@/types/common';
import { getContactRolesBySource } from '@/bundles/Contact/helpers/cards/contactsCard';
import { FilterBusinessLineAll, FilterBusinessLineItems } from '@/bundles/GroupFilters/enums/FilterBusinessLineEnum';
import { getSaleConditionsTextValues } from '@/bundles/Comparables/enums/SaleConditionsEnum';
import { getPropertyCenterTypeTextValues } from '@/bundles/Property/enums/PropertyCenterTypeEnum';
import { isYearRule } from '@/bundles/GroupFilters/helpers/rules';
import { getShowArchiveFilterConfig } from '@/bundles/GroupFilters/helpers/filtersConfig/getShowArchiveFilterConfig';
import { getStaticSetFilterConfig } from '@/bundles/GroupFilters/helpers/filtersConfig/getStaticSetFilterConfig';
import { FilterUnsubscribedButtonItems } from '@/bundles/GroupFilters/consts/labels/FilterUnsubscribedLabels';

export const getContactFilters = (filters?: Nullable<IGroupFilterItem>): IFilterConfigItem[] => {
  const propertyTypesWithMixedUse = getPropertyTypesWithMixedUseForFilters();
  const selectedPropertyTypes = filters?.[PROPERTY_TYPE_KEY]?.value || [];

  return [
    // FilterCategoryEnum.contact_details start
    {
      title: 'Company Type',
      name: 'company_my_office_enterprise',
      type: FilterTypeEnum.button_group,
      items: <IFilterButtonGroupItems>FilterOfficeEnterpriseItems,
      category: FilterCategoryEnum.contact_details,
      additional: {
        mandatory: false,
      }
    },
    {
      title: 'Contact Name',
      name: 'contact_name',
      type: FilterTypeEnum.textfield,
      category: FilterCategoryEnum.contact_details,
    },
    {
      title: 'Company Title',
      name: 'contact_title',
      type: FilterTypeEnum.textfield,
      category: FilterCategoryEnum.contact_details,
    },
    {
      title: 'Specialization',
      name: 'contact_specialization',
      type: FilterTypeEnum.specialization_multiple_select,
      category: FilterCategoryEnum.contact_details,
    },
    {
      title: 'Contact Email',
      name: 'contact_has_email',
      items: [
        { text: 'All Contacts', value: 'all' },
        { text: 'Only contacts with an email', value: 'yes' },
        { text: 'Only contact without an email', value: 'no' },
      ],
      type: FilterTypeEnum.one_select,
      category: FilterCategoryEnum.contact_details,
    },
    {
      title: 'Contact Tracked',
      name: 'contact_tracked',
      type: FilterTypeEnum.switch,
      category: FilterCategoryEnum.contact_details,
    },
    {
      title: 'Contact Has Notes',
      name: 'contact_has_notes',
      type: FilterTypeEnum.switch,
      category: FilterCategoryEnum.contact_details,
    },
    {
      title: 'Contact Notes Last Edited Date',
      name: 'contact_notes_edited_date',
      type: FilterTypeEnum.date_range,
      category: FilterCategoryEnum.contact_details,
    },
    {
      title: 'Contact Notes Last Edited By',
      name: 'contact_notes_edited_by',
      type: FilterTypeEnum.create_updated_by_select,
      category: FilterCategoryEnum.contact_details,
    },
    {
      title: 'Unsubscribed',
      name: 'contact_unsubscribed',
      type: FilterTypeEnum.button_group,
      items: <IFilterButtonGroupItems>FilterUnsubscribedButtonItems,
      category: FilterCategoryEnum.contact_details,
      additional: {
        mandatory: false,
      }
    },
    {
      title: i18n.t(LocalizationEnum.legacy_id) as string,
      name: 'contact_legacy_id',
      type: FilterTypeEnum.textfield,
      category: FilterCategoryEnum.contact_details,
    },
    {
      title: 'Contact City',
      name: 'contact_location_city',
      type: FilterTypeEnum.city_autocomplete,
      category: FilterCategoryEnum.contact_details,
      additional: {
        citiesSource: 'contact',
      }
    },
    {
      title: 'Contact State',
      name: 'contact_location_state',
      type: FilterTypeEnum.states_autocomplete,
      category: FilterCategoryEnum.contact_details,
    },
    {
      title: 'Contact Country',
      name: 'contact_location_country',
      type: FilterTypeEnum.autocomplete_multiple_select,
      items: getCountriesForFilters(),
      category: FilterCategoryEnum.contact_details,
    },
    {
      title: 'Contact Zip / Postal Code',
      name: 'contact_location_postal_cd',
      type: FilterTypeEnum.postal_code_combobox,
      category: FilterCategoryEnum.contact_details,
    },
    // FilterCategoryEnum.contact_details end
    // FilterCategoryEnum.office_address start
    {
      title: 'Office City',
      name: 'contact_office_city',
      type: FilterTypeEnum.city_autocomplete,
      category: FilterCategoryEnum.office_address,
      additional: {
        citiesSource: 'contact',
      }
    },
    {
      title: 'Office State',
      name: 'contact_office_state',
      type: FilterTypeEnum.states_autocomplete,
      category: FilterCategoryEnum.office_address,
    },
    {
      title: 'Office Country',
      name: 'contact_office_country',
      type: FilterTypeEnum.autocomplete_multiple_select,
      items: getCountriesForFilters(),
      category: FilterCategoryEnum.office_address,
    },
    {
      title: 'Office Postal',
      name: 'contact_office_postal',
      type: FilterTypeEnum.postal_code_combobox,
      category: FilterCategoryEnum.office_address,
    },
    // FilterCategoryEnum.office_address end
    // FilterCategoryEnum.polygon_search start
    {
      title: 'Search by',
      name: 'contact_spatial_search_record_type',
      type: FilterTypeEnum.one_select,
      items: [
        { text: 'Contact by Contact Location', value: ContactSpatialSearchRecordTypeEnum.contact },
        { text: 'Contact by Property Location', value: ContactSpatialSearchRecordTypeEnum.property },
        { text: 'Property by Property Location', value: ContactSpatialSearchRecordTypeEnum.propertyPins },
      ],
      category: FilterCategoryEnum.polygon_search,
    },
    // FilterCategoryEnum.polygon_search end
    // FilterCategoryEnum.company_filters start
    {
      title: 'Company Name',
      name: 'company_name',
      type: FilterTypeEnum.company_name_combobox,
      category: FilterCategoryEnum.company_filters,
    },
    {
      title: 'Company Annual Revenue',
      name: 'company_annual_revenue',
      type: FilterTypeEnum.currency_range,
      category: FilterCategoryEnum.company_filters,
    },
    {
      title: 'Brokerage Company Filter',
      name: 'company_is_brokerage',
      type: FilterTypeEnum.button_group,
      items: <IFilterButtonGroupItems>FilterBrokerageItems,
      category: FilterCategoryEnum.company_filters,
      additional: {
        mandatory: false
      }
    },
    {
      title: 'Company Role',
      name: 'contact_company_role',
      type: FilterTypeEnum.autocomplete_multiple_select,
      items: getContactRolesBySource('company'),
      category: FilterCategoryEnum.company_filters,
    },
    {
      title: 'Company Industry Group',
      name: 'company_industry_group',
      items: getIndustryTypes(),
      type: FilterTypeEnum.autocomplete_multiple_select,
      category: FilterCategoryEnum.company_filters,
    },
    // FilterCategoryEnum.company_filters end
    // FilterCategoryEnum.occupier_filters start
    {
      title: 'Occupier Contact Role',
      name: 'occupier_contact_role',
      type: FilterTypeEnum.autocomplete_multiple_select,
      items: getContactRolesBySource('occupier'),
      category: FilterCategoryEnum.occupier_filters,
    },
    {
      title: 'Occupier Space Size',
      name: 'occupier_size',
      type: FilterTypeEnum.number_range,
      category: FilterCategoryEnum.occupier_filters,
      formatter: (v: Nullable<number>) => formatToSF(v),
    },
    {
      title: 'Occupier Land Size',
      name: 'occupier_land_size', // sending with occupier_land_size_unit
      type: FilterTypeEnum.land_size_range,
      category: FilterCategoryEnum.occupier_filters,
    },
    {
      title: 'Occupier Occupancy Type',
      name: 'occupier_occupancy_type',
      type: FilterTypeEnum.autocomplete_multiple_select,
      items: getOccupancyTypeFilterItems(),
      category: FilterCategoryEnum.occupier_filters,
    },
    {
      title: 'Occupier Lease Expiration Date',
      name: 'occupier_lease_expiration_date',
      type: FilterTypeEnum.date_range,
      category: FilterCategoryEnum.occupier_filters,
    },
    {
      title: 'Occupier Lease Expiration in',
      name: 'occupier_lease_expiration',
      type: FilterTypeEnum.one_select,
      items: [
        { text: '3 months', value: '3 months' },
        { text: '6 months', value: '6 months' },
        { text: '9 months', value: '9 months' },
        { text: '12 months', value: '12 months' },
        { text: '18 months', value: '18 months' },
        { text: '21 months', value: '21 months' },
        { text: '24 months', value: '24 months' },
      ],
      category: FilterCategoryEnum.occupier_filters,
    },
    {
      title: 'Apply spatial search for occupiers',
      name: 'occupier_polygon_switch',
      type: FilterTypeEnum.switch,
      category: FilterCategoryEnum.occupier_filters,
    },
    {
      title: 'Occupier Lease Commencement Date',
      name: 'occupier_lease_commencement',
      type: FilterTypeEnum.date_range,
      category: FilterCategoryEnum.occupier_filters,
    },
    {
      title: 'Occupier Lease Execution Date',
      name: 'occupier_lease_execution',
      type: FilterTypeEnum.date_range,
      category: FilterCategoryEnum.occupier_filters,
    },
    {
      title: '3PL Type',
      name: 'occupier_tpl_type',
      type: FilterTypeEnum.autocomplete_multiple_select,
      items: Object.keys(occupierTplTypeLabels).map((value) => ({
        text: occupierTplTypeLabels[value],
        value,
      })),
      category: FilterCategoryEnum.occupier_filters,
    },
    {
      title: 'Inventory Amount PSF',
      name: 'occupier_inventory_per_sf',
      type: FilterTypeEnum.currency_range,
      category: FilterCategoryEnum.occupier_filters,
    },
    {
      title: 'Inventory Amount Total',
      name: 'occupier_inventory_total',
      type: FilterTypeEnum.currency_range,
      category: FilterCategoryEnum.occupier_filters,
    },
    {
      title: 'Occupier Property Size',
      name: 'occupier_property_size',
      type: FilterTypeEnum.number_range,
      category: FilterCategoryEnum.occupier_filters,
      formatter: (v: Nullable<number>) => formatToSF(v),
    },
    {
      title: 'Occupier Property Land Size',
      name: 'occupier_property_land_size', // sending with occupier_property_land_size_unit
      type: FilterTypeEnum.land_size_range,
      category: FilterCategoryEnum.occupier_filters,
    },
    {
      title: 'Occupier Property Contact Role',
      name: 'occupier_property_contact_role',
      type: FilterTypeEnum.autocomplete_multiple_select,
      items: getContactRolesBySource('property'),
      category: FilterCategoryEnum.occupier_filters,
    },
    {
      title: 'Occupier Property Type',
      type: FilterTypeEnum.property_type_group,
      category: FilterCategoryEnum.occupier_filters,
      children: [
        {
          title: 'Occupier Property Type',
          name: `occupier_${PROPERTY_TYPE_KEY}`,
          type: FilterTypeEnum.autocomplete_multiple_select,
          items: propertyTypesWithMixedUse,
        },
        {
          title: 'Include Occupier Property Type As Primary Use',
          name: 'occupier_property_type_as_primary_use',
          type: FilterTypeEnum.switch,
          defaultValue: true,
        },
      ],
    },
    {
      title: 'Occupier Property Use',
      type: FilterTypeEnum.property_use_group,
      category: FilterCategoryEnum.occupier_filters,
      children: [
        {
          title: 'Occupier Property Use Class',
          name: 'occupier_property_use_class',
          type: FilterTypeEnum.button_group,
          items: FilterPrimaryUseClassItems,
          defaultValue: FilterPrimaryUseClassEnum.either,
        },
        {
          title: 'Occupier Property Use',
          name: 'occupier_property_primary_use',
          type: FilterTypeEnum.autocomplete_multiple_select,
          items: getPropertyUsesForFilters(),
        },
      ],
    },
    {
      title: 'Occupier Property Use Type(s)',
      name: 'occupier_property_use_type',
      type: FilterTypeEnum.use_type_multiple_select,
      category: FilterCategoryEnum.occupier_filters,
    },
    {
      title: 'Occupier Property Rail Status',
      items: getRailsStatusItems(),
      name: 'occupier_property_rail_status',
      type: FilterTypeEnum.autocomplete_multiple_select,
      category: FilterCategoryEnum.occupier_filters,
    },
    {
      title: 'Occupier Property Construction Status',
      items: [
        { text: 'Proposed', value: 'proposed' },
        { text: 'Under Construction', value: 'under construction' },
        { text: 'Existing', value: 'existing' },
      ],
      name: 'occupier_property_construction_status',
      type: FilterTypeEnum.autocomplete_multiple_select,
      category: FilterCategoryEnum.occupier_filters,
    },
    {
      title: 'Occupier Property City',
      name: 'occupier_property_location_city',
      type: FilterTypeEnum.city_autocomplete,
      category: FilterCategoryEnum.occupier_filters,
      additional: {
        citiesSource: 'property',
      },
    },
    {
      title: 'Occupier Property State',
      name: 'occupier_property_location_state',
      type: FilterTypeEnum.states_autocomplete,
      category: FilterCategoryEnum.occupier_filters,
    },
    {
      title: 'Occupier Property Country',
      name: 'occupier_property_location_country',
      type: FilterTypeEnum.autocomplete_multiple_select,
      items: getCountriesForFilters(),
      category: FilterCategoryEnum.occupier_filters,
    },
    {
      title: 'Occupier Property Zip / Postal Code',
      name: 'occupier_property_location_postal_cd',
      type: FilterTypeEnum.postal_code_combobox,
      category: FilterCategoryEnum.occupier_filters,
    },
    ...getMarketsFilters({
      propertyTypes: selectedPropertyTypes,
      sourceName: 'occupier_property',
      category: FilterCategoryEnum.occupier_filters,
      titlePrefix: 'Occupier Property',
    }),
    {
      title: `Occupier Property ${i18n.t(LocalizationEnum.apn_number)}`,
      name: 'occupier_property_apn_number',
      type: FilterTypeEnum.textfield,
      category: FilterCategoryEnum.occupier_filters,
    },
    // FilterCategoryEnum.occupier_filters end
    // FilterCategoryEnum.property_filters start
    {
      title: 'Property Size',
      name: 'property_size',
      type: FilterTypeEnum.number_range,
      category: FilterCategoryEnum.property_filters,
      formatter: (v: Nullable<number>) => formatToSF(v),
    },
    {
      title: 'Land Size',
      name: 'property_land_size', // sending with property_land_size_unit
      type: FilterTypeEnum.land_size_range,
      category: FilterCategoryEnum.property_filters,
    },
    {
      title: 'Property Contact Role',
      name: 'property_contact_role',
      type: FilterTypeEnum.autocomplete_multiple_select,
      items: getContactRolesBySource('property'),
      category: FilterCategoryEnum.property_filters,
    },
    {
      title: 'Property Type',
      type: FilterTypeEnum.property_type_group,
      category: FilterCategoryEnum.property_filters,
      children: [
        {
          title: 'Property Type',
          name: PROPERTY_TYPE_KEY,
          type: FilterTypeEnum.autocomplete_multiple_select,
          items: propertyTypesWithMixedUse,
        },
        {
          title: 'Include Property Type As Primary Use',
          name: 'property_type_as_primary_use',
          type: FilterTypeEnum.switch,
          defaultValue: true,
        },
      ],
    },
    {
      title: 'Property Use',
      type: FilterTypeEnum.property_use_group,
      category: FilterCategoryEnum.property_filters,
      children: [
        {
          title: 'Property Use Class',
          name: 'property_use_class',
          type: FilterTypeEnum.button_group,
          items: FilterPrimaryUseClassItems,
          defaultValue: FilterPrimaryUseClassEnum.either,
        },
        {
          title: 'Property Use',
          name: 'property_primary_use',
          type: FilterTypeEnum.autocomplete_multiple_select,
          items: getPropertyUsesForFilters(),
        },
      ],
    },
    {
      title: 'Property Use Type(s)',
      name: 'property_use_type',
      type: FilterTypeEnum.use_type_multiple_select,
      category: FilterCategoryEnum.property_filters,
    },
    {
      title: 'Property Rail Status',
      items: getRailsStatusItems(),
      name: 'property_rail_status',
      type: FilterTypeEnum.autocomplete_multiple_select,
      category: FilterCategoryEnum.property_filters,
    },
    {
      title: 'Property Construction Status',
      items: [
        { text: 'Proposed', value: 'proposed' },
        { text: 'Under Construction', value: 'under construction' },
        { text: 'Existing', value: 'existing' },
      ],
      name: 'property_construction_status',
      type: FilterTypeEnum.autocomplete_multiple_select,
      category: FilterCategoryEnum.property_filters,
    },
    // FilterCategoryEnum.property_filters end
    // FilterCategoryEnum.market_location start
    {
      title: 'Address',
      name: 'property_address',
      type: FilterTypeEnum.textfield,
      category: FilterCategoryEnum.market_location,
    },
    {
      title: 'City',
      name: 'property_location_city',
      type: FilterTypeEnum.city_autocomplete,
      category: FilterCategoryEnum.market_location,
      additional: {
        citiesSource: 'property',
      }
    },
    {
      title: 'State',
      name: 'property_location_state',
      type: FilterTypeEnum.states_autocomplete,
      category: FilterCategoryEnum.market_location,
    },
    {
      title: 'Country',
      name: 'property_location_country',
      type: FilterTypeEnum.autocomplete_multiple_select,
      items: getCountriesForFilters(),
      category: FilterCategoryEnum.market_location,
    },
    {
      title: 'Zip / Postal Code',
      name: 'property_location_postal_cd',
      type: FilterTypeEnum.postal_code_combobox,
      category: FilterCategoryEnum.market_location,
    },
    ...getMarketsFilters({ propertyTypes: selectedPropertyTypes }),
    {
      title: `Property ${i18n.t(LocalizationEnum.apn_number)}`,
      name: 'property_apn_number',
      type: FilterTypeEnum.textfield,
      category: FilterCategoryEnum.market_location,
    },
    // FilterCategoryEnum.market_location end
    // FilterCategoryEnum.availability_filters start
    {
      title: `Availability Contact Role`,
      name: 'availability_contact_role',
      type: FilterTypeEnum.autocomplete_multiple_select,
      items: getContactRolesBySource('availability'),
      category: FilterCategoryEnum.availability_filters,
    },
    {
      title: `Availability Status`,
      name: 'availability_status',
      type: FilterTypeEnum.autocomplete_multiple_select,
      items: [
        { text: 'Active', value: 'active' },
        { text: 'Inactive', value: 'inactive' },
        { text: 'Leased', value: 'leased' },
        { text: 'Pending', value: 'pending' },
        { text: 'Sold', value: 'sold' },
      ],
      category: FilterCategoryEnum.availability_filters,
    },
    {
      title: 'Transaction Type',
      type: FilterTypeEnum.transaction_type_group,
      category: FilterCategoryEnum.availability_filters,
      children: [
        {
          title: 'Transaction Type',
          name: 'contact_transaction_type',
          items: <IFilterButtonGroupItems>Object.keys(FilterTransactionTypeLabels).map((key) => ({
            text: FilterTransactionTypeLabels[key],
            value: key,
          })),
          defaultValue: FilterTransactionTypeEnum.all,
          type: FilterTypeEnum.button_group,
        },
        {
          title: 'Sublease',
          name: 'contact_sublease',
          items: <IFilterButtonGroupItems>FilterSubleaseButtonItems,
          defaultValue: FilterAllExcludeFilterEnum.all,
          type: FilterTypeEnum.button_group,
        },
        {
          title: 'Investment Sale',
          name: 'contact_investment',
          items: <IFilterButtonGroupItems>FilterInvestmentSaleButtonItems,
          defaultValue: FilterAllExcludeFilterEnum.all,
          type: FilterTypeEnum.button_group,
        },
      ],
    },
    {
      title: `Available Space`,
      name: 'availability_size',
      type: FilterTypeEnum.number_range,
      category: FilterCategoryEnum.availability_filters,
      formatter: (v: Nullable<number>) => formatToSF(v),
    },
    {
      title: `Land Size`,
      name: 'availability_land_size', // sending with availability_land_price_unit
      type: FilterTypeEnum.land_size_range,
      category: FilterCategoryEnum.availability_filters,
    },
    {
      title: `Lease Rate Starting`,
      name: 'availability_lease_rate',
      type: FilterTypeEnum.currency_range,
      category: FilterCategoryEnum.availability_filters,
      additional: {
        suffix: '/ SF',
      }
    },
    {
      title: 'Sale Price',
      name: 'availability_sales_price',
      type: FilterTypeEnum.sale_price_range, // sending with availability_sales_price_unit
      category: FilterCategoryEnum.availability_filters,
    },
    // FilterCategoryEnum.availability_filters end
    // FilterCategoryEnum.pipeline_filters start
    {
      title: 'Pipeline Stage',
      name: 'pipeline_stage',
      items: Object.entries(pipelineStageLabels).map<ITextValue<number>>((
        [value, text]) => ({ text, value: Number(value) }
      )),
      type: FilterTypeEnum.autocomplete_multiple_select,
      category: FilterCategoryEnum.pipeline_filters,
    },
    {
      title: 'Pipeline Transaction Category',
      name: 'pipeline_category',
      items: [
        { text: 'Tenant Representation', value: 'Tenant Representation' },
        { text: 'Buyer Representation', value: 'Buyer Representation' },
        { text: 'Landlord Representation', value: 'Landlord Representation' },
        { text: 'Seller Representation', value: 'Seller Representation' },
        { text: 'Corporate Services', value: 'Corporate Services' },
        { text: 'Consulting', value: 'Consulting' },
      ],
      type: FilterTypeEnum.autocomplete_multiple_select,
      category: FilterCategoryEnum.pipeline_filters,
    },
    {
      title: 'Pipeline Contact Role',
      name: 'pipeline_contact_role',
      items: [...getContactRolesBySource('pipeline'), ...getContactRolesBySource('pipeline_primary_client')],
      type: FilterTypeEnum.autocomplete_multiple_select,
      category: FilterCategoryEnum.pipeline_filters,
    },
    // FilterCategoryEnum.pipeline_filters end
    // FilterCategoryEnum.deal_filters start
    {
      title: i18n.t(LocalizationEnum.business_line) as string,
      name: 'deal_business_line',
      type: FilterTypeEnum.button_group,
      items: <IFilterButtonGroupItems>FilterBusinessLineItems,
      defaultValue: FilterBusinessLineAll,
      category: FilterCategoryEnum.deal_filters,
    },
    {
      title: 'Transaction Type',
      type: FilterTypeEnum.transaction_type_group,
      category: FilterCategoryEnum.deal_filters,
      children: [
        {
          title: 'Transaction Type',
          name: 'deal_transaction_type',
          items: <IFilterButtonGroupItems>Object.keys(FilterTransactionTypeLabels).map((key) => ({
            text: FilterTransactionTypeLabels[key],
            value: key,
          })),
          type: FilterTypeEnum.button_group,
          additional: {
            mandatory: false,
          }
        },
        {
          title: 'Sublease',
          name: 'deal_sublease',
          items: <IFilterButtonGroupItems>FilterSubleaseButtonItems,
          type: FilterTypeEnum.button_group,
          additional: {
            mandatory: false,
          }
        },
        {
          title: 'Investment Sale',
          name: 'deal_investment',
          items: <IFilterButtonGroupItems>FilterInvestmentSaleButtonItems,
          type: FilterTypeEnum.button_group,
          additional: {
            mandatory: false,
          }
        },
        {
          title: 'Consulting',
          name: 'deal_consulting',
          items: <IFilterButtonGroupItems>FilterConsultingButtonItems,
          type: FilterTypeEnum.button_group,
          additional: {
            mandatory: false,
          }
        },
      ],
    },
    {
      title: 'Property Type',
      type: FilterTypeEnum.property_type_group,
      category: FilterCategoryEnum.deal_filters,
      children: [
        {
          title: 'Property Type',
          name: PROPERTY_TYPE_KEY,
          type: FilterTypeEnum.autocomplete_multiple_select,
          items: propertyTypesWithMixedUse,
        },
        {
          title: 'Include Property Type As Primary Use',
          name: 'property_type_as_primary_use',
          type: FilterTypeEnum.switch,
          defaultValue: true,
        },
      ],
    },
    {
      title: i18n.t(LocalizationEnum.sale_conditions) as string,
      items: getSaleConditionsTextValues(),
      name: 'deal_sale_conditions',
      type: FilterTypeEnum.autocomplete_multiple_select,
      category: FilterCategoryEnum.deal_filters,
    },
    {
      title: 'Property Use',
      type: FilterTypeEnum.property_use_group,
      category: FilterCategoryEnum.deal_filters,
      children: [
        {
          title: 'Property Use Class',
          name: 'property_use_class',
          type: FilterTypeEnum.button_group,
          items: FilterPrimaryUseClassItems,
          defaultValue: FilterPrimaryUseClassEnum.either,
        },
        {
          title: 'Property Use',
          name: 'property_primary_use',
          type: FilterTypeEnum.autocomplete_multiple_select,
          items: getPropertyUsesForFilters(),
        },
      ],
    },
    {
      title: 'Property Use Type(s)',
      name: 'property_use_type',
      type: FilterTypeEnum.use_type_multiple_select,
      category: FilterCategoryEnum.deal_filters,
    },
    {
      title: i18n.t(LocalizationEnum.retail_center_type) as string,
      items: getPropertyCenterTypeTextValues(),
      name: 'property_center_type',
      type: FilterTypeEnum.autocomplete_multiple_select,
      category: FilterCategoryEnum.deal_filters,
    },
    {
      title: 'Deal Transaction Date',
      name: 'deal_close_transaction_date',
      type: FilterTypeEnum.date_range,
      category: FilterCategoryEnum.deal_filters,
    },
    {
      title: 'Deal Transaction Year',
      name: 'deal_close_year',
      type: FilterTypeEnum.number_range,
      category: FilterCategoryEnum.deal_filters,
      additional: {
        minRules: [isYearRule],
        maxRules: [isYearRule],
        isRawFormat: true,
      },
      formatter: (v: Nullable<number>) => v?.toString() || '', // don't format
    },
    {
      title: 'Deal Transaction Category',
      name: 'deal_category',
      type: FilterTypeEnum.transaction_category_checkbox_group,
      category: FilterCategoryEnum.deal_filters,
    },
    {
      title: 'Deal Size',
      name: 'deal_size',
      type: FilterTypeEnum.number_range,
      category: FilterCategoryEnum.deal_filters,
      formatter: (v: Nullable<number>) => formatToSF(v),
    },
    {
      title: 'Deal Land Size',
      name: 'deal_land_size',
      type: FilterTypeEnum.land_size_range, // sending with deal_land_size_unit
      category: FilterCategoryEnum.deal_filters,
    },
    {
      title: 'Deal Units / Keys',
      name: 'deal_units',
      type: FilterTypeEnum.number_range,
      category: FilterCategoryEnum.deal_filters,
    },
    {
      title: 'Deal Status',
      name: 'deal_status',
      type: FilterTypeEnum.autocomplete_multiple_select,
      items: [
        { text: 'Pending', value: 'pending' },
        { text: 'Approved', value: 'approved' },
        { text: 'Invoiced', value: 'invoiced' },
        { text: 'Paid', value: 'paid' }
      ],
      category: FilterCategoryEnum.deal_filters,
    },
    {
      title: 'Contract Pending Status',
      name: 'deal_contract_pending',
      type: FilterTypeEnum.one_select,
      items: [
        { text: 'All Deals', value: 'all' },
        { text: 'Contract Pending Only', value: 'pending' },
        { text: 'Exclude Contract Pending', value: 'notPending' }
      ],
      category: FilterCategoryEnum.deal_filters,
    },
    {
      title: 'Brokers',
      name: 'deal_broker',
      type: FilterTypeEnum.brokers_select,
      category: FilterCategoryEnum.deal_filters,
    },
    {
      title: 'Deal Contact Role',
      name: 'contact_deal_role',
      items: getContactRolesBySource('deal'),
      type: FilterTypeEnum.autocomplete_multiple_select,
      category: FilterCategoryEnum.deal_filters,
    },
    // FilterCategoryEnum.deal_filters end
    // FilterCategoryEnum.created_edited start
    {
      title: 'Created/Edited Date',
      name: 'contact_view_date',
      type: FilterTypeEnum.date_range,
      category: FilterCategoryEnum.created_edited,
    },
    {
      title: 'Created Date',
      name: 'contact_created_date',
      type: FilterTypeEnum.date_range,
      category: FilterCategoryEnum.created_edited,
    },
    {
      title: 'Edited Date',
      name: 'contact_edited_date',
      type: FilterTypeEnum.date_range,
      category: FilterCategoryEnum.created_edited,
    },
    {
      title: 'Created/Edited by',
      name: 'contact_view',
      type: FilterTypeEnum.create_updated_by_select, // should be handled in mappers to old filters
      category: FilterCategoryEnum.created_edited,
    },
    {
      title: 'Created by',
      name: 'contact_created_by',
      type: FilterTypeEnum.create_updated_by_select, // should be handled in mappers to old filters
      category: FilterCategoryEnum.created_edited,
    },
    {
      title: 'Edited by',
      name: 'contact_edited_by',
      type: FilterTypeEnum.create_updated_by_select, // should be handled in mappers to old filters
      category: FilterCategoryEnum.created_edited,
    },
    // FilterCategoryEnum.created_edited end
    getShowArchiveFilterConfig(),
    getStaticSetFilterConfig('contact'),
  ].filter(Boolean) as IFilterConfigItem[]; // filter out null values
};
